import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton
} from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import AddBoxOutlinedIcon from '@mui/icons-material/AddBoxOutlined';
import IndeterminateCheckBoxOutlinedIcon from '@mui/icons-material/IndeterminateCheckBoxOutlined';
import { useTranslation } from 'react-i18next';
import { FolderData } from '../types/types';
import { alpha } from '@mui/material/styles';

const VDR_DRAG_TYPE = 'application/x-vdr-item';
const VDR_FOLDER_DRAG_TYPE = 'application/x-vdr-folder';

type DragPayload =
  | {
      type: 'file';
      fileId: string;
      fileName: string;
      sourceFolderId: string;
    }
  | {
      type: 'folder';
      folderId: string;
      folderName: string;
      parentFolderId: string | null;
    };

interface Props {
  folders: FolderData[];
  selectedId: string;
  onSelect: (folderId: string) => void;
  onMoveFile?: (fileId: string, sourceFolderId: string, targetFolderId: string) => void | Promise<void>;
  onMoveFolder?: (folderId: string, targetParentFolderId: string | null) => void | Promise<void>;
  onReorderFolders?: (
    parentFolderId: string | null,
    orderedFolderIds: string[]
  ) => void | Promise<void>;
  accessType?: 'ADMIN' | 'MEMBER';
}

const createDragPreview = (label: string) => {
  const preview = document.createElement('div');

  preview.style.position = 'fixed';
  preview.style.top = '-1000px';
  preview.style.left = '-1000px';
  preview.style.display = 'flex';
  preview.style.alignItems = 'center';
  preview.style.gap = '8px';
  preview.style.maxWidth = '320px';
  preview.style.padding = '8px 12px';
  preview.style.background = '#ffffff';
  preview.style.border = '1px solid rgba(0, 0, 0, 0.18)';
  preview.style.borderRadius = '6px';
  preview.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.18)';
  preview.style.fontFamily = 'Barlow, sans-serif';
  preview.style.fontSize = '14px';
  preview.style.color = '#333333';
  preview.style.whiteSpace = 'nowrap';
  preview.style.overflow = 'hidden';
  preview.style.textOverflow = 'ellipsis';
  preview.style.pointerEvents = 'none';
  preview.style.zIndex = '99999';

  const icon = document.createElement('span');
  icon.textContent = '■';
  icon.style.color = '#73AF73';
  icon.style.fontSize = '16px';
  icon.style.lineHeight = '1';

  const text = document.createElement('span');
  text.textContent = label;
  text.style.overflow = 'hidden';
  text.style.textOverflow = 'ellipsis';

  preview.appendChild(icon);
  preview.appendChild(text);
  document.body.appendChild(preview);

  return preview;
};

const FolderPanel: React.FC<Props> = ({
  folders,
  selectedId,
  onSelect,
  onMoveFile,
  onMoveFolder,
  onReorderFolders,
  accessType = 'MEMBER'
}) => {
  const { t, i18n } = useTranslation();

  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [dragOverFolderId, setDragOverFolderId] = useState<string | null>(null);
  const [dragOverPosition, setDragOverPosition] = useState<'before' | 'inside' | 'after' | null>(null);
  const [dragOverRoot, setDragOverRoot] = useState(false);
  const [draggedFolder, setDraggedFolder] = useState<{
    folderId: string;
    parentFolderId: string | null;
  } | null>(null);

  useEffect(() => {
    const storedLang = localStorage.getItem('language') || 'EN';
    i18n.changeLanguage(storedLang);
  }, []);

  useEffect(() => {
    const existingIds = new Set<string>();

    const collectIds = (folderList: FolderData[]) => {
      folderList.forEach(folder => {
        existingIds.add(folder.folderId);
        collectIds(folder.subfolders || []);
      });
    };

    collectIds(folders);

    setExpanded(prev => {
      const next = new Set(
        Array.from(prev).filter(folderId => existingIds.has(folderId))
      );

      folders.forEach(folder => next.add(folder.folderId));
      return next;
    });
  }, [folders]);

  const toggleExpand = (folderId: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(folderId)) {
        next.delete(folderId);
      } else {
        next.add(folderId);
      }
      return next;
    });
  };

  const hasDragType = (types: readonly string[] | DOMStringList, type: string) =>
    Array.from(types).includes(type);

  const readPayload = (event: React.DragEvent): DragPayload | null => {
    const raw = event.dataTransfer.getData(VDR_DRAG_TYPE);
    if (!raw) return null;

    try {
      return JSON.parse(raw) as DragPayload;
    } catch {
      return null;
    }
  };

  const reorderSiblingFolder = (
    siblingFolders: FolderData[],
    parentFolderId: string | null,
    sourceFolderId: string,
    targetFolderId: string,
    position: 'before' | 'after'
  ) => {
    if (sourceFolderId === targetFolderId) return;

    const source = siblingFolders.find(folder => folder.folderId === sourceFolderId);
    if (!source) return;

    const reordered = siblingFolders.filter(folder => folder.folderId !== sourceFolderId);
    const targetIndex = reordered.findIndex(folder => folder.folderId === targetFolderId);

    if (targetIndex < 0) return;

    const insertAt = position === 'before' ? targetIndex : targetIndex + 1;
    reordered.splice(insertAt, 0, source);

    void onReorderFolders?.(
      parentFolderId,
      reordered.map(folder => folder.folderId)
    );
  };

  const handleDropOnFolder = (
    event: React.DragEvent,
    targetFolderId: string,
    parentFolderId: string | null,
    siblingFolders: FolderData[]
  ) => {
    if (accessType !== 'ADMIN') return;

    event.preventDefault();
    event.stopPropagation();

    const dropPosition = dragOverPosition || 'inside';

    setDragOverFolderId(null);
    setDragOverPosition(null);
    setDragOverRoot(false);

    const payload = readPayload(event);
    if (!payload) return;

    if (
      payload.type === 'folder' &&
      payload.parentFolderId === parentFolderId &&
      (dropPosition === 'before' || dropPosition === 'after')
    ) {
      reorderSiblingFolder(
        siblingFolders,
        parentFolderId,
        payload.folderId,
        targetFolderId,
        dropPosition
      );
      return;
    }

    setExpanded(prev => new Set(prev).add(targetFolderId));

    if (payload.type === 'file') {
      if (payload.sourceFolderId !== targetFolderId) {
        void onMoveFile?.(payload.fileId, payload.sourceFolderId, targetFolderId);
      }
      return;
    }

    if (payload.folderId !== targetFolderId) {
      void onMoveFolder?.(payload.folderId, targetFolderId);
    }
  };

  const handleDropOnRoot = (event: React.DragEvent) => {
    if (accessType !== 'ADMIN') return;

    event.preventDefault();
    event.stopPropagation();

    setDragOverFolderId(null);
    setDragOverPosition(null);
    setDragOverRoot(false);

    const payload = readPayload(event);
    if (!payload || payload.type !== 'folder') return;

    void onMoveFolder?.(payload.folderId, null);
  };

  const renderFolderTree = (
    folderList: FolderData[],
    level = 0,
    parentFolderId: string | null = null
  ) => {
    return folderList.map(folder => {
      const hasChildren = folder.subfolders && folder.subfolders.length > 0;
      const isExpanded = expanded.has(folder.folderId);
      const isDropTarget = dragOverFolderId === folder.folderId;

      return (
        <React.Fragment key={folder.folderId}>
          <ListItemButton
            selected={folder.folderId === selectedId}
            draggable={accessType === 'ADMIN'}
            onDragStart={(event) => {
              if (accessType !== 'ADMIN') {
                event.preventDefault();
                return;
              }

              event.dataTransfer.effectAllowed = 'move';
              event.dataTransfer.setData(
                VDR_DRAG_TYPE,
                JSON.stringify({
                  type: 'folder',
                  folderId: folder.folderId,
                  folderName: folder.folderName,
                  parentFolderId,
                })
              );
              event.dataTransfer.setData(VDR_FOLDER_DRAG_TYPE, '1');
              event.dataTransfer.setData('text/plain', folder.folderName);

              setDraggedFolder({
                folderId: folder.folderId,
                parentFolderId,
              });

              const preview = createDragPreview(folder.folderName);
              event.dataTransfer.setDragImage(preview, 18, 18);

              window.setTimeout(() => {
                preview.remove();
              }, 0);
            }}
            onDragEnd={() => {
              setDragOverFolderId(null);
              setDragOverPosition(null);
              setDragOverRoot(false);
              setDraggedFolder(null);
            }}
            onDragOver={(event) => {
              if (
                accessType !== 'ADMIN' ||
                !hasDragType(event.dataTransfer.types, VDR_DRAG_TYPE)
              ) {
                return;
              }

              event.preventDefault();
              event.stopPropagation();
              event.dataTransfer.dropEffect = 'move';
              setDragOverRoot(false);
              setDragOverFolderId(folder.folderId);

              const canReorderSiblings =
                draggedFolder !== null &&
                draggedFolder.folderId !== folder.folderId &&
                draggedFolder.parentFolderId === parentFolderId;

              if (canReorderSiblings) {
                const rect = event.currentTarget.getBoundingClientRect();
                const relativeY = event.clientY - rect.top;
                const ratio = relativeY / rect.height;

                if (ratio < 0.28) {
                  setDragOverPosition('before');
                } else if (ratio > 0.72) {
                  setDragOverPosition('after');
                } else {
                  setDragOverPosition('inside');
                }
              } else {
                setDragOverPosition('inside');
              }
            }}
            onDragLeave={(event) => {
              const relatedTarget = event.relatedTarget as Node | null;
              if (!relatedTarget || !event.currentTarget.contains(relatedTarget)) {
                setDragOverFolderId(current =>
                  current === folder.folderId ? null : current
                );
                setDragOverPosition(null);
              }
            }}
            onDrop={(event) =>
              handleDropOnFolder(
                event,
                folder.folderId,
                parentFolderId,
                folderList
              )
            }
            sx={{
              pl: 2 + level * 2,
              gap: 1,
              cursor: accessType === 'ADMIN' ? 'grab' : 'pointer',
              userSelect: 'none',
              position: 'relative',
              ...(isDropTarget && dragOverPosition === 'inside' && {
                bgcolor: alpha('#73AF73', 0.18),
                outline: `2px solid ${alpha('#73AF73', 0.65)}`,
                outlineOffset: '-2px',
              }),
              ...(isDropTarget && dragOverPosition === 'before' && {
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  left: 12,
                  right: 12,
                  top: 0,
                  height: '3px',
                  bgcolor: '#73AF73',
                  borderRadius: 2,
                },
              }),
              ...(isDropTarget && dragOverPosition === 'after' && {
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  left: 12,
                  right: 12,
                  bottom: 0,
                  height: '3px',
                  bgcolor: '#73AF73',
                  borderRadius: 2,
                },
              }),
            }}
            onClick={() => onSelect(folder.folderId)}
          >
            {hasChildren ? (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleExpand(folder.folderId);
                }}
              >
                {isExpanded ? (
                  <IndeterminateCheckBoxOutlinedIcon fontSize="small" />
                ) : (
                  <AddBoxOutlinedIcon fontSize="small" />
                )}
              </IconButton>
            ) : (
              <Box sx={{ width: 28 }} />
            )}

            <ListItemIcon sx={{ minWidth: 32 }}>
              <FolderIcon
                sx={{
                  color: level === 0 ? '#73AF73' : alpha('#73AF73', 0.5)
                }}
              />
            </ListItemIcon>
            <ListItemText primary={folder.folderName} />
          </ListItemButton>

          {hasChildren &&
            isExpanded &&
            renderFolderTree(folder.subfolders, level + 1, folder.folderId)}
        </React.Fragment>
      );
    });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box
        onDragOver={(event) => {
          if (
            accessType !== 'ADMIN' ||
            !hasDragType(event.dataTransfer.types, VDR_FOLDER_DRAG_TYPE)
          ) {
            return;
          }

          event.preventDefault();
          event.dataTransfer.dropEffect = 'move';
          setDragOverFolderId(null);
          setDragOverPosition(null);
          setDragOverRoot(true);
        }}
        onDragLeave={() => setDragOverRoot(false)}
        onDrop={handleDropOnRoot}
        sx={{
          display: 'flex',
          alignItems: 'center',
          p: 2,
          ...(dragOverRoot && {
            bgcolor: alpha('#73AF73', 0.18),
            outline: `2px solid ${alpha('#73AF73', 0.65)}`,
            outlineOffset: '-2px',
          }),
        }}
      >
        <FolderIcon sx={{ mr: 1, color: '#73AF73' }} />
        <Typography variant="h6" fontWeight="bold" sx={{ color: '#73AF73' }}>
          {t('folders')}
        </Typography>
      </Box>

      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          maxHeight: 'calc(100vh - 80px)',
        }}
      >
        <List>
          {renderFolderTree(folders)}
        </List>
      </Box>
    </Box>
  );
};

export default FolderPanel;
